<template>
  <div class="h-full flex flex-col items-center py-8 px-4">
    <div class="mobile-padding"></div>
    <div class="liquidGlassCard rounded-3xl w-full h-full p-6 pt-14 flex flex-col overflow-hidden">
      <!-- Back Button -->
      <button
        class="absolute w-8 h-8 left-5 top-5"
        @click="$router.back"
      >
        <ColorImg
          src="/images/icons/back.png"
          class="bg-current w-full h-full"
          alt="Retour"
        />
      </button>

      <!-- Action Button in Top Right -->
      <div
        v-if="activeTab === 'accounts'"
        class="absolute w-auto px-4 py-2 bg-primary/10 text-primary rounded-xl right-5 top-5 cursor-pointer font-bold flex items-center gap-2 hover:bg-primary/20 transition-all"
        @click="openAddAccountModal"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
        </svg>
        <span class="text-sm">Ajouter un compte</span>
      </div>

      <div
        v-else
        class="absolute w-auto px-4 py-2 bg-primary/10 text-primary rounded-xl right-5 top-5 cursor-pointer font-bold flex items-center gap-2 hover:bg-primary/20 transition-all"
        @click="openWithdrawalModal()"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
        </svg>
        <span class="text-sm">Nouveau retrait</span>
      </div>

      <!-- Page Title -->
      <div class="absolute left-16 text-2xl top-5 title-color font-bold">
        Transferts
      </div>

      <div class="mb-4"></div>

      <!-- Navigation Tabs -->
      <div class="flex gap-2 p-1.5 bg-gray-100 rounded-2xl mb-4 self-start">
        <button
          @click="activeTab = 'accounts'"
          class="px-4 py-2 rounded-xl text-xs font-bold transition-all"
          :class="activeTab === 'accounts' ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'"
        >
          Comptes de transfert
        </button>
        <button
          @click="activeTab = 'transactions'"
          class="px-4 py-2 rounded-xl text-xs font-bold transition-all"
          :class="activeTab === 'transactions' ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'"
        >
          Transferts effectués
        </button>
      </div>

      <!-- Tab 1: Accounts List -->
      <div v-if="activeTab === 'accounts'" class="w-full h-full overflow-y-auto">
        <DataFetcher dataKey="withdrawalAccounts" ref="accountsGetter">
          <template #any="{ loading, loadingError, refresh, data }">
            <div v-if="loading" class="flex flex-col gap-4 px-2 w-full h-full">
              <div
                v-for="i in 4"
                :key="i"
                class="shimmerContainer h-24 rounded-2xl w-full"
              ></div>
            </div>

            <div
              v-else-if="loadingError"
              class="flex flex-col items-center justify-center p-8 text-center w-full h-full"
            >
              <div class="title-color font-bold mb-2">Erreur de chargement</div>
              <button @click="refresh" class="text-xs text-primary font-bold underline">Réessayer</button>
            </div>

            <div
              v-else-if="!data || data.length === 0"
              class="flex flex-col items-center justify-center p-12 text-center w-full h-full"
            >
              <div class="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
              </div>
              <div class="text-xl font-bold title-color mb-1">Aucun compte de transfert</div>
              <p class="text-xs text-gray-500 mb-4">Ajoutez un compte Mobile Money pour recevoir vos retraits</p>
              <button
                @click="openAddAccountModal"
                class="px-5 py-3 rounded-2xl bg-primary text-white font-bold text-xs shadow-lg shadow-primary/30 active:scale-95 transition-all"
              >
                + Ajouter un compte
              </button>
            </div>

            <div v-else class="w-full flex flex-col gap-3 px-1">
              <div
                v-for="account in data"
                :key="account.id"
                class="p-4 rounded-2xl border border-gray-100 bg-white/70 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3"
              >
                <div class="flex items-center gap-3">
                  <div class="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
                    {{ (account.operator || 'MM').slice(0, 3).toUpperCase() }}
                  </div>
                  <div>
                    <h4 class="font-bold title-color text-sm">{{ account.accountLabel }}</h4>
                    <div class="text-xs text-gray-500 font-mono tracking-wide mt-0.5">
                      {{ account.accountNumber }} • <span class="uppercase font-semibold text-gray-700">{{ account.operator }}</span>
                    </div>
                  </div>
                </div>

                <div class="flex items-center gap-2 justify-end">
                  <span class="text-xs px-2.5 py-1 bg-green-50 text-green-700 font-semibold rounded-lg border border-green-200">
                    Vérifié ✓
                  </span>
                  <button
                    @click="openWithdrawalModal(account)"
                    class="px-3.5 py-2 rounded-xl bg-primary text-white font-bold text-xs shadow-sm hover:bg-primary-600 transition-all active:scale-95"
                  >
                    Retirer
                  </button>
                </div>
              </div>
            </div>
          </template>
        </DataFetcher>
      </div>

      <!-- Tab 2: Transactions History -->
      <div v-else class="w-full h-full overflow-y-auto">
        <DataFetcher dataKey="withdrawals" ref="withdrawalsGetter">
          <template #any="{ loading, loadingError, refresh, data }">
            <div v-if="loading" class="flex flex-col gap-4 px-2 w-full h-full">
              <div
                v-for="i in 5"
                :key="i"
                class="shimmerContainer h-24 rounded-2xl w-full"
              ></div>
            </div>

            <div
              v-else-if="loadingError"
              class="flex flex-col items-center justify-center p-8 text-center w-full h-full"
            >
              <div class="title-color font-bold mb-2">Erreur de chargement</div>
              <button @click="refresh" class="text-xs text-primary font-bold underline">Réessayer</button>
            </div>

            <div
              v-else-if="!data || data.length === 0"
              class="flex flex-col items-center justify-center p-12 text-center w-full h-full"
            >
              <div class="text-xl font-bold title-color">Aucun transfert effectué</div>
              <p class="text-xs text-gray-500 mt-1">Vos transferts mobile money apparaîtront ici</p>
            </div>

            <div v-else class="w-full h-full overflow-y-auto px-2">
              <div v-for="item in data" :key="item.id" class="mb-4 border-b border-gray-100 pb-3">
                <div class="flex justify-between items-start gap-4 p-1">
                  <div class="text-lg title-color">
                    <div class="flex gap-2 items-center">
                      <ColorImg
                        src="/images/icons/calendar.png"
                        class="bg-current w-4 h-4 opacity-80"
                      />
                      <div class="first-letter:uppercase text-xs font-medium">
                        {{ Commons.time(item.date, 'D MMM YYYY - hh:mm') }}
                      </div>
                    </div>
                    <div class="flex gap-2 items-center mt-1.5">
                      <ColorImg
                        src="/images/icons/phone.png"
                        class="bg-current w-4 h-4 opacity-80"
                      />
                      <div class="first-letter:uppercase text-xs font-mono font-medium">
                        +{{ item.phoneNumber }}
                      </div>
                    </div>
                  </div>

                  <div class="flex flex-col items-end gap-1.5 text-right">
                    <button
                      class="!py-0.5 !px-2.5 buttonComponent small flex items-center justify-center text-xs rounded-lg font-bold"
                      :class="getStatusColor(item.status)"
                    >
                      {{ item.statusDisplay }}
                    </button>

                    <div class="flex flex-col gap-0.5 mt-0.5">
                      <div class="whitespace-nowrap title-color font-black text-sm">
                        Retiré : {{ Commons.formatNumber(item.fees?.srcAmount || item.amount, { suffix: ' FCFA' }) }}
                      </div>
                      <div class="text-[11px] font-semibold text-emerald-600">
                        Déposé : {{ Commons.formatNumber(item.fees?.dstAmount || item.amount, { suffix: ' FCFA' }) }}
                      </div>
                      <div class="text-[11px] font-medium text-red-500">
                        Frais : {{ Commons.formatNumber(item.fees?.totalFeeCalculated || 0, { suffix: ' FCFA' }) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </DataFetcher>
      </div>
    </div>

    <!-- Modals -->
    <WithdrawalModal />
    <AddNewWithdrawalAccount />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import DataFetcher from '@/features/data/presentation/components/DataFetcher.vue'
import Commons from '@/shared/utils/commons'
import ColorImg from '@/presentation/components/img/ColorImg.vue'
import WithdrawalModal from './components/WithdrawalModal.vue'
import AddNewWithdrawalAccount from '../withdrawal/AddNewWithdrawalAccount.vue'
import Events from '@/events/events'

const activeTab = ref('accounts')
const withdrawalsGetter = ref(null)
const accountsGetter = ref(null)

onMounted(() => {
  Events.handle('withdrawal_launched', () => {
    if (withdrawalsGetter.value && withdrawalsGetter.value.silentRefresh) {
      withdrawalsGetter.value.silentRefresh()
    }
  })

  Events.handle('withdrawal_account_created', () => {
    if (accountsGetter.value && accountsGetter.value.silentRefresh) {
      accountsGetter.value.silentRefresh()
    } else if (accountsGetter.value && accountsGetter.value.refresh) {
      accountsGetter.value.refresh()
    }
  })
})

function getStatusColor(status) {
  if (status === 'completed' || status === 'paid' || status === 'success')
    return 'text-green-600 bg-green-600/20'
  if (status === 'failed') return 'text-red-600 bg-red-600/20'
  if (status === 'initiated' || status === 'pending')
    return 'text-orange-500 bg-orange-500/20'
  return 'text-gray-500 bg-gray-500/20'
}

function openWithdrawalModal(account = null) {
  Events.dispatch('open_withdrawal_modal', account)
}

function openAddAccountModal() {
  Events.dispatch('open_add_withdrawal_account')
}
</script>